import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { ChartContainer } from "../ui/chart";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  AlertTriangle,
  Zap,
} from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { useZonePagination } from "../../../hooks/basic-reports/use-meter-pagination";
import MeterPagination from "./MeterPagination";
import ExportButton from "./ExportButton";
import { CHIP_COLORS } from "../utils/colors";
import { useReportData } from "../../../contexts/ReportDataContext";

const chartConfig = {
  demandKw: { label: "Demand (kW)", color: CHIP_COLORS.info },
  demandKva: { label: "Demand (kVA)", color: CHIP_COLORS.pending },
  peak: { label: "Peak (kW)", color: CHIP_COLORS.high },
  avg: { label: "Average (kW)", color: CHIP_COLORS.good },
};

const ITEMS_PER_PAGE = 10;

const DemandReports = () => {
  const { reports, loading, error, progress } = useReportData();
  const [showMeterTable, setShowMeterTable] = useState(true);
  const { getPage, setPage } = useZonePagination();

  if (loading)
    return (
      <div className="p-10 text-center">
        <div className="animate-pulse text-muted-foreground">
          {progress || "Loading Report Data..."}
        </div>
      </div>
    );
  if (error)
    return <div className="p-10 text-center text-destructive">{error}</div>;
  if (!reports)
    return <div className="p-10 text-center">No data available</div>;

  const { demand } = reports;

  // Demand trend from all data points
  const demandTrend = demand.demandTrend.map((d) => ({
    time: d.time,
    demandKw: d.kw,
    demandKva: d.kva,
    label: d.time,
  }));

  // Daily peak analysis
  const dailyPeakData = demand.dailyPeak.map((d) => {
    const avgEntry = demand.dailyAvg.find((a) => a.date === d.date);
    return {
      day: d.label,
      peak: d.value,
      avg: avgEntry ? avgEntry.value : 0,
    };
  });

  // Per-meter demand data
  const perMeterData = demand.perMeter;

  // Pagination
  const currentPage = getPage("meters");
  const totalPages = Math.ceil(perMeterData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, perMeterData.length);
  const paginatedMeters = perMeterData.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <TrendingUp className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Peak Demand</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-lg font-bold">
                    {demand.peakKW.toLocaleString()} kW
                  </p>
                  <Badge variant="outline" className="text-xs">
                    {demand.peakKVA.toLocaleString()} kVA
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-info/10">
                <Activity className="w-4 h-4 text-info-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Load Factor</p>
                <p className="text-lg font-bold">{demand.loadFactor}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-warning/10">
                <AlertTriangle className="w-4 h-4 text-warning" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  Contract Violations
                </p>
                <p className="text-lg font-bold">{demand.violations}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success/10">
                <Zap className="w-4 h-4 text-success" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg Demand</p>
                <p className="text-lg font-bold">
                  {demand.avgKW.toLocaleString()} kW
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reports Row */}
      <div className="grid grid-cols-12 gap-6">
        <Card className="col-span-8">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold">
                  Demand Trend
                </CardTitle>
                <CardDescription>
                  Active Power (kW) vs Apparent Power (kVA)
                </CardDescription>
              </div>
              <Badge variant="outline" className="text-xs">
                Time Series
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[280px] w-full">
              <LineChart
                data={demandTrend}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  opacity={0.5}
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={30}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={40}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-background border rounded-lg shadow-lg p-3">
                          <p className="font-medium text-xs mb-2">
                            {payload[0].payload.label}
                          </p>
                          <div className="space-y-1">
                            {payload.map((entry: any) => (
                              <div
                                key={entry.name}
                                className="flex items-center gap-2 text-xs"
                              >
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: entry.color }}
                                />
                                <span className="text-muted-foreground">
                                  {entry.name
                                    .replace("demand", "")
                                    .replace(/kw/i, "kW")
                                    .replace(/kva/i, "kVA")}
                                  :
                                </span>
                                <span className="font-mono font-medium">
                                  {entry.value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend
                  verticalAlign="top"
                  height={36}
                  iconType="circle"
                  formatter={(value) => (
                    <span className="text-xs text-muted-foreground">
                      {value
                        .replace("demand", "")
                        .replace(/kw/i, "kW")
                        .replace(/kva/i, "kVA")}
                    </span>
                  )}
                />
                <Line
                  type="monotone"
                  dataKey="demandKw"
                  stroke={CHIP_COLORS.info}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="demandKva"
                  stroke={CHIP_COLORS.pending}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card className="col-span-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">
              Daily Peak Analysis
            </CardTitle>
            <CardDescription>Peak vs Average Demand</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={dailyPeakData}
                  margin={{ top: 10, right: 0, left: -20, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    opacity={0.5}
                  />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "hsl(var(--muted)/0.2)" }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-background border rounded-lg shadow-lg p-3">
                            <p className="font-medium text-xs mb-2">
                              {payload[0].payload.day}
                            </p>
                            <div className="space-y-1">
                              {payload.map((entry: any) => (
                                <div
                                  key={entry.name}
                                  className="flex items-center gap-2 text-xs"
                                >
                                  <div
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <span className="text-muted-foreground capitalize">
                                    {entry.name}:
                                  </span>
                                  <span className="font-mono font-medium">
                                    {Number(entry.value).toLocaleString()} kW
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="peak"
                    name="Peak"
                    fill={CHIP_COLORS.high}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                  />
                  <Bar
                    dataKey="avg"
                    name="Average"
                    fill={CHIP_COLORS.good}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Per-Meter Demand Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">
                Per-Meter Demand Summary
              </CardTitle>
              <CardDescription>
                Detailed demand analytics for {perMeterData.length} meters
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <ExportButton reportName="Meter Demand Summary" />
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-primary"
                onClick={() => setShowMeterTable(!showMeterTable)}
              >
                {showMeterTable ? "Hide Details" : "Show Details"}
              </Button>
            </div>
          </div>
        </CardHeader>
        {showMeterTable && (
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Meter ID</TableHead>
                  <TableHead className="text-right">Max kW</TableHead>
                  <TableHead className="text-right">Max kVA</TableHead>
                  <TableHead className="text-right">Avg kW</TableHead>
                  <TableHead className="text-right">Load Factor</TableHead>
                  {/* <TableHead className="text-right">MD Resets</TableHead> */}
                  <TableHead className="text-right">Peak Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedMeters.map((meter) => (
                  <TableRow key={meter.meterId} className="hover:bg-muted/50">
                    <TableCell className="font-mono text-sm">
                      {meter.meterId.replace(/^Demo1_/, "")}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {meter.maxKW.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.maxKVA.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.avgKW.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.loadFactor}
                    </TableCell>
                    {/* <TableCell className="text-right">
                      <Badge variant="outline">{meter.mdResets}</Badge>
                    </TableCell> */}
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {meter.peakTime}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <MeterPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={perMeterData.length}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={(page) => setPage("meters", page)}
              hasNextPage={currentPage < totalPages}
              hasPrevPage={currentPage > 1}
            />
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default DemandReports;
